import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/RequireAuth';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ErrorPanel } from './components/common/ErrorPanel';
import { PageShell } from './components/common/PageShell';
import { Spinner } from './components/common/Spinner';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * Shown when the meeting chunk cannot be fetched even after a reload: at that
 * point the person deserves an explanation and a button, not a spinner.
 */
function StaleBuildPage() {
  return (
    <PageShell>
      <ErrorPanel
        title="The app was updated"
        message="This tab was open across an update and could not load the meeting page. Reloading usually fixes it."
        onRetry={() => window.location.reload()}
        // No way out but the reload. There used to be a link to the landing
        // page; `/` is the operator login now, which is not somewhere to send
        // a patient whose meeting chunk failed to load.
      />
    </PageShell>
  );
}

/** Rate-limits the automatic reload below so it can never loop. */
const CHUNK_RELOAD_AT = 'chunk-reload-at';

/**
 * How recently a reload must have happened for the next failure to be treated
 * as a loop rather than a fresh staleness.
 *
 * Long enough to cover a reload plus the new index and chunk downloading on a
 * slow connection, so a genuine recovery is never mistaken for a loop. Short
 * enough that a second, unrelated deploy later in the same sitting still gets
 * its own automatic reload rather than being told to press a button.
 */
const RELOAD_GUARD_MS = 15_000;

/**
 * `sessionStorage` reads and writes, which throw rather than return null when
 * storage is unavailable.
 *
 * Safari in private browsing and a browser configured to block site data both
 * throw on access, and this code runs inside a `catch` that is the last thing
 * standing between the user and a blank screen. An exception here would
 * escape the handler and take the fallback page down with it, turning a
 * recoverable stale deploy into nothing at all. Failing closed is the right
 * default: with no way to record that a reload happened, not reloading is
 * safer than reloading in a loop we cannot count.
 */
function readReloadStamp(): number {
  try {
    return Number(sessionStorage.getItem(CHUNK_RELOAD_AT) ?? 0);
  } catch {
    // Reported as a reload that just happened, so the guard blocks.
    return Date.now();
  }
}

function writeReloadStamp(): boolean {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_AT, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * The meeting page is code-split.
 *
 * `livekit-client` is by far the largest dependency in the app. Loading it
 * lazily means the landing page, the one people hit on a phone, on mobile
 * data, before they have decided to join anything, ships without it, and the
 * WebRTC bundle downloads only once someone actually opens a meeting URL.
 *
 * The catch handles a deploy happening under an open tab. Chunk filenames are
 * hashed, so a tab whose index predates the deploy asks for a file that no
 * longer exists and the import rejects. The tab is not broken, only stale:
 * one reload fetches the new index and everything lines up again. The
 * timestamp guard keeps a genuinely broken deploy from reloading forever;
 * inside the guard window the failure surfaces as a page instead.
 */
/**
 * The console is its own chunks: nobody joining a call downloads any of it.
 * The login page is not lazy — it is the first paint of the bare domain.
 */
const AdminLayout = lazy(() =>
  import('./pages/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const DashboardPage = lazy(() =>
  import('./pages/admin/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const TenantsPage = lazy(() =>
  import('./pages/admin/TenantsPage').then((m) => ({ default: m.TenantsPage })),
);
const SessionsPage = lazy(() =>
  import('./pages/admin/SessionsPage').then((m) => ({ default: m.SessionsPage })),
);
const RecordingsPage = lazy(() =>
  import('./pages/admin/RecordingsPage').then((m) => ({ default: m.RecordingsPage })),
);
const UsersPage = lazy(() =>
  import('./pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const TestCallPage = lazy(() =>
  import('./pages/admin/TestCallPage').then((m) => ({ default: m.TestCallPage })),
);
const AccountPasswordPage = lazy(() =>
  import('./pages/admin/AccountPasswordPage').then((m) => ({
    default: m.AccountPasswordPage,
  })),
);

const MeetingPage = lazy(
  (): Promise<{ default: ComponentType }> =>
    import('./pages/MeetingPage')
      .then((module) => ({ default: module.MeetingPage }))
      .catch(() => {
        // The stamp has to be written before reloading, and the write has to
        // be seen to succeed. Reloading on the strength of a write that
        // silently failed is the loop this guard exists to prevent.
        if (Date.now() - readReloadStamp() > RELOAD_GUARD_MS && writeReloadStamp()) {
          window.location.reload();
          // Never resolves: the page is already on its way down, and the
          // Suspense fallback covers the moment it takes to go.
          return new Promise<never>(() => {});
        }
        return { default: StaleBuildPage };
      }),
);

/**
 * Route table.
 *
 * Two halves. `/meeting/:meetingId` is public and is the only URL that has to
 * be shareable: paste it anywhere, open it in any browser, and you are in that
 * room. Everything else is the operator console, behind a login — including
 * `/`, which is why a visitor to the bare domain sees a sign-in form.
 *
 * The shape is the boundary. The auth provider is a layout route wrapping only
 * the console, so the meeting path cannot be gated by it even by accident.
 *
 * `BrowserRouter` (not hash routing) means Nginx must fall back to index.html
 * for unknown paths — see `try_files $uri $uri/ /index.html` in video.conf.
 */
export function App() {
  return (
    // Inside the router, not outside it. The fallback renders `PageShell`,
    // which contains a `Link`, and a `Link` without router context throws:
    // placing the boundary above `BrowserRouter` made its own fallback crash
    // and produced the blank page it exists to prevent. Everything that can
    // realistically throw is a route, so this still covers the whole app.
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense
          fallback={
            <PageShell>
              <Spinner label="Loading…" />
            </PageShell>
          }
        >
          <Routes>
            {/*
              Outside the auth provider, deliberately: a patient opening a
              visit link must never wait on an operator-session probe, and must
              never be redirected by one.
            */}
            <Route path="/meeting/:meetingId" element={<MeetingPage />} />
            {/* A bare or trailing-slash /meeting link is a broken link, not
                a reason to show a stranger the login form. */}
            <Route path="/meeting" element={<NotFoundPage />} />

            <Route element={<AuthProvider />}>
              <Route path="/login" element={<LoginPage />} />
              <Route element={<RequireAuth />}>
                <Route element={<AdminLayout />}>
                  {/* The console opens on the dashboard; tenant management
                      moved to /tenants when the dashboard took the front. */}
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/tenants" element={<TenantsPage />} />
                  <Route path="/sessions" element={<SessionsPage />} />
                  <Route path="/recordings" element={<RecordingsPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/test-call" element={<TestCallPage />} />
                  <Route path="/account/password" element={<AccountPasswordPage />} />
                </Route>
              </Route>
            </Route>

            {/* Where the panel used to live; keeps old bookmarks working. */}
            <Route path="/admin" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
